import React from 'react';

import { Messages } from '~src/codegen/Localize';

import { PoolAssignmentOption } from '../../types/PoolAssignmentOption';
import { PoolID } from '../../types/PoolID';
import { RoutingFilterItem } from '../../types/RoutingFilterItem';
import { FilterGroup, GroupedRoutingFilterView } from '../groupedView/GroupedRoutingFilterView';

export type PoolGroupedRoutingFilterViewProps = Readonly<{
  filters: ReadonlyArray<RoutingFilterItem>;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  isLoading: boolean;
}>;

function groupFiltersByPool(
  filters: ReadonlyArray<RoutingFilterItem>,
  poolOptions: ReadonlyArray<PoolAssignmentOption>
): FilterGroup[] {
  const validPoolIds = new Set<string>(poolOptions.map(opt => opt.poolID as string));
  const poolMap: Record<string, RoutingFilterItem[]> = {};
  const poolNameMap: Record<string, string> = {};

  for (const opt of poolOptions) {
    const id = opt.poolID as string;
    poolMap[id] = [];
    poolNameMap[id] = opt.poolDisplayName;
  }

  for (const filter of filters) {
    // Group by pool only for ALLOW filters. BLOCK filters have no pool and collapse
    // into the shared "No/Blocked Pool" bucket. Deciding on isBlocked — rather than on
    // whether poolId matches a known option — keeps ALLOW filters whose pool was deleted
    // (or hasn't loaded yet) grouped under their own pool instead of the blocked bucket.
    const pid = filter.isBlocked ? '' : (filter.poolId || '');
    if (!poolMap[pid]) {
      poolMap[pid] = [];
      // A non-blocked filter with a pool that isn't a known option (stale/deleted) keeps
      // its raw id as the label; only the empty key represents the "No/Blocked Pool" group.
      poolNameMap[pid] = pid === '' ? Messages.bridgeSiteSettingsBlockedPoolGroupLabel() : pid;
    }
    poolMap[pid].push(filter);
  }

  const groups: FilterGroup[] = [];
  for (const id of Object.keys(poolMap)) {
    if (poolMap[id].length === 0) {
      continue;
    }
    groups.push({
      key: id,
      label: poolNameMap[id] || id,
      filters: poolMap[id],
      // Only pre-fill the create dialog when the pool is a real, selectable option —
      // otherwise the Add button would seed a stale/deleted poolID.
      preFill: validPoolIds.has(id) ? { poolID: id as PoolID } : undefined,
    });
  }

  return groups;
}

export function PoolGroupedRoutingFilterView(props: PoolGroupedRoutingFilterViewProps) {
  const { filters, poolOptions, isLoading } = props;

  const groups = React.useMemo(() => groupFiltersByPool(filters, poolOptions), [filters, poolOptions]);

  return (
    <GroupedRoutingFilterView
      groupHeaderLabel={Messages.bridgeSiteSettingsPoolName()}
      countHeaderLabel={Messages.bridgeSiteSettingsRoutingFiltersColumn()}
      groups={groups}
      isLoading={isLoading}
      testIdPrefix={'pool-routing-filters'}
    />
  );
}
